import * as THREE from "three";
import { SceneManager } from "./SceneManager";
import type { PlacedInstance } from "./InstancedRenderer";
import { BLOCK, type BlockId, type VoxelWorld } from "../types";
import { BLOCK_SIZE, PORTAL_FLOOD_MS, PORTAL_DRAIN_MS } from "../config";

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
const clamp01 = (t: number) => Math.min(Math.max(t, 0), 1);

export function portalCentroid(world: VoxelWorld): THREE.Vector3 | null {
    const half = (world.size * BLOCK_SIZE) / 2;
    let sx = 0, sy = 0, sz = 0, n = 0;
    for (let x = 0; x < world.size; x++) {
        for (let z = 0; z < world.size; z++) {
            const offset = world.columnOffset(x, z);
            for (let y = 0; y < world.height; y++) {
                if ((world.data[offset + y] as BlockId) !== BLOCK.Portal) continue;
                sx += x * BLOCK_SIZE - half + BLOCK_SIZE / 2;
                sy += y * BLOCK_SIZE;
                sz += z * BLOCK_SIZE - half + BLOCK_SIZE / 2;
                n++;
            }
        }
    }
    return n > 0 ? new THREE.Vector3(sx / n, sy / n, sz / n) : null;
}

const SPAWN_TINT = new THREE.Color(0.8, 1.0, 0.88);
const SPAWN_EDGE = 3;
const SPAWN_GLOW = 1.8;
const SPAWN_DURATION_MS = 650;

/**
 * Reveals a freshly built world as a ring of light sweeping outward from the
 * center, brightening each block into its true color as it passes. Recolors
 * already-placed, already-culled instances only (never touches position or
 * scale), so it can't expose faces the per-instance face-culling trimmed away
 * for a block's final, settled spot.
 */
export function animateMaterialize(scene: SceneManager, placed: PlacedInstance[]): () => void {
    const perMesh = new Map<THREE.InstancedMesh, Float32Array>();
    const items: { mesh: THREE.InstancedMesh; index: number; dist: number }[] = [];
    for (const p of placed) {
        if (!p.mesh.instanceColor) continue;
        if (!perMesh.has(p.mesh)) {
            perMesh.set(p.mesh, Float32Array.from(p.mesh.instanceColor.array as Float32Array));
        }
        items.push({ mesh: p.mesh, index: p.index, dist: Math.hypot(p.px, p.pz) });
    }
    const maxDist = items.reduce((m, i) => Math.max(m, i.dist), 1) + SPAWN_EDGE + 1;

    let raf = 0;
    let start: number | null = null;

    const settle = () => {
        perMesh.forEach((orig, mesh) => {
            (mesh.instanceColor!.array as Float32Array).set(orig);
            mesh.instanceColor!.needsUpdate = true;
        });
        scene.render();
    };

    const apply = (elapsed: number): boolean => {
        const t = clamp01(elapsed / SPAWN_DURATION_MS);
        const R = easeOutCubic(t) * maxDist;
        for (const it of items) {
            const orig = perMesh.get(it.mesh)!;
            const arr = it.mesh.instanceColor!.array as Float32Array;
            const o = it.index * 3;
            const f = clamp01((R - it.dist) / SPAWN_EDGE);
            const rim = Math.max(0, 1 - Math.abs(it.dist - R) / SPAWN_EDGE);
            const boost = 1 + SPAWN_GLOW * rim;
            arr[o] = (SPAWN_TINT.r + (orig[o] - SPAWN_TINT.r) * f) * boost;
            arr[o + 1] = (SPAWN_TINT.g + (orig[o + 1] - SPAWN_TINT.g) * f) * boost;
            arr[o + 2] = (SPAWN_TINT.b + (orig[o + 2] - SPAWN_TINT.b) * f) * boost;
        }
        perMesh.forEach((_, mesh) => {
            mesh.instanceColor!.needsUpdate = true;
        });
        scene.render();
        return t >= 1;
    };

    const loop = (now: number) => {
        if (start === null) start = now;
        if (!apply(now - start)) raf = requestAnimationFrame(loop);
        else settle();
    };

    apply(0);
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
}

const FLOOD_VIOLET = new THREE.Color(0.62, 0.28, 1.0);
const FLOOD_EDGE = 3;
const FLOOD_GLOW = 1.8;
const FLOOD_FLASH = 1.6;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2);
const smooth = (t: number) => t * t * (3 - 2 * t);

export function animatePortalFlood(
    scene: SceneManager,
    placed: PlacedInstance[],
    origin: THREE.Vector3,
    mode: "flood" | "drain",
    onDone?: () => void,
): () => void {
    const duration = mode === "flood" ? PORTAL_FLOOD_MS : PORTAL_DRAIN_MS;

    const perMesh = new Map<
        THREE.InstancedMesh,
        { orig: Float32Array; target: [number, number, number] }
    >();
    const items: { mesh: THREE.InstancedMesh; index: number; dist: number }[] = [];
    for (const p of placed) {
        if (!p.mesh.instanceColor) continue;
        if (!perMesh.has(p.mesh)) {
            const mat = p.mesh.material as THREE.MeshStandardMaterial;
            perMesh.set(p.mesh, {
                orig: Float32Array.from(p.mesh.instanceColor.array as Float32Array),
                target: [
                    Math.min(FLOOD_VIOLET.r / Math.max(mat.color.r, 0.02), 8),
                    Math.min(FLOOD_VIOLET.g / Math.max(mat.color.g, 0.02), 8),
                    Math.min(FLOOD_VIOLET.b / Math.max(mat.color.b, 0.02), 8),
                ],
            });
        }
        items.push({
            mesh: p.mesh,
            index: p.index,
            dist: Math.hypot(p.px - origin.x, p.py - origin.y, p.pz - origin.z),
        });
    }
    const maxDist = items.reduce((m, i) => Math.max(m, i.dist), 1) + FLOOD_EDGE + 1;

    let raf = 0;
    let start: number | null = null;
    let finished = false;

    const apply = (elapsed: number): boolean => {
        const t = clamp01(elapsed / duration);
        const e = mode === "flood" ? easeOutCubic(t) : easeInOutCubic(t);
        const R = (mode === "flood" ? e : 1 - e) * maxDist;
        const flash =
            FLOOD_FLASH *
            (mode === "flood"
                ? smooth(clamp01((t - 0.7) / 0.3))
                : 1 - smooth(clamp01(t / 0.35)));
        for (const it of items) {
            const data = perMesh.get(it.mesh)!;
            const arr = it.mesh.instanceColor!.array as Float32Array;
            const o = it.index * 3;
            if (mode === "drain" && t >= 1) {
                arr[o] = data.orig[o];
                arr[o + 1] = data.orig[o + 1];
                arr[o + 2] = data.orig[o + 2];
                continue;
            }
            const f = clamp01((R - it.dist) / FLOOD_EDGE);
            const rim = Math.max(0, 1 - Math.abs(it.dist - R) / FLOOD_EDGE);
            const boost = 1 + FLOOD_GLOW * rim + flash;
            arr[o] = (data.orig[o] + (data.target[0] - data.orig[o]) * f) * boost;
            arr[o + 1] = (data.orig[o + 1] + (data.target[1] - data.orig[o + 1]) * f) * boost;
            arr[o + 2] = (data.orig[o + 2] + (data.target[2] - data.orig[o + 2]) * f) * boost;
        }
        perMesh.forEach((_, mesh) => {
            mesh.instanceColor!.needsUpdate = true;
        });
        scene.render();
        return t >= 1;
    };

    const loop = (now: number) => {
        if (start === null) start = now;
        if (!apply(now - start)) {
            raf = requestAnimationFrame(loop);
        } else {
            finished = true;
            onDone?.();
        }
    };

    apply(0);
    raf = requestAnimationFrame(loop);
    return () => {
        if (!finished) cancelAnimationFrame(raf);
    };
}
